import React, { useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface HeaderLabelWithTooltipProps {
  label: React.ReactNode;
  tooltip?: string;
  className?: string;
  /** Extra classes on the floating tooltip (e.g. max width for longer copy). */
  tooltipClassName?: string;
}

const DEFAULT_TOOLTIP_CLASS =
  "pointer-events-none fixed z-[9999] -translate-x-1/2 whitespace-nowrap rounded bg-gray-font px-3 py-2 text-xs text-neutral shadow-lg";

export const TABLE_TOOLTIP_CLASS = DEFAULT_TOOLTIP_CLASS;

type TableActionTooltipProps = {
  label: string;
  children: React.ReactNode;
};

export function TableActionTooltip({
  label,
  children,
}: TableActionTooltipProps): React.ReactElement {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) {
      return;
    }

    const rect = anchor.getBoundingClientRect();
    setPosition({
      top: rect.bottom + 8,
      left: rect.left + rect.width / 2,
    });
  }, []);

  const showTooltip = useCallback(() => {
    updatePosition();
    setVisible(true);
  }, [updatePosition]);

  const hideTooltip = useCallback(() => {
    setVisible(false);
  }, []);

  return (
    <>
      <span
        ref={anchorRef}
        className="inline-flex"
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
      >
        {children}
      </span>

      {visible &&
        createPortal(
          <div
            className={DEFAULT_TOOLTIP_CLASS}
            style={{ top: position.top, left: position.left }}
            role="tooltip"
          >
            {label}
            <div className="absolute left-1/2 bottom-full h-0 w-0 -translate-x-1/2 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-font" />
          </div>,
          document.body,
        )}
    </>
  );
}

export default function HeaderLabelWithTooltip({
  label,
  tooltip,
  className = "",
  tooltipClassName = DEFAULT_TOOLTIP_CLASS,
}: HeaderLabelWithTooltipProps): React.ReactElement {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) {
      return;
    }

    const rect = anchor.getBoundingClientRect();
    setPosition({
      top: rect.bottom + 8,
      left: rect.left + rect.width / 2,
    });
  }, []);

  const showTooltip = useCallback(() => {
    updatePosition();
    setVisible(true);
  }, [updatePosition]);

  const hideTooltip = useCallback(() => {
    setVisible(false);
  }, []);

  if (!tooltip) {
    return <span className={className}>{label}</span>;
  }

  return (
    <>
      <div
        ref={anchorRef}
        className={`inline-flex max-w-full cursor-help ${className}`}
        aria-label={tooltip}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
      >
        <span className="truncate">{label}</span>
      </div>

      {visible &&
        createPortal(
          <div
            className={tooltipClassName}
            style={{ top: position.top, left: position.left }}
            role="tooltip"
          >
            {tooltip}
            <div className="absolute left-1/2 bottom-full h-0 w-0 -translate-x-1/2 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-font" />
          </div>,
          document.body,
        )}
    </>
  );
}

export function getHeaderAriaLabel(
  header: string | React.ReactNode,
  headerId: string,
  headerTooltip?: string,
): string {
  if (headerTooltip) {
    return headerTooltip;
  }
  if (typeof header === "string") {
    return header;
  }
  return headerId;
}
